import { useEffect, useState } from 'react';
import { motion, useScroll, useSpring } from 'motion/react';
import { Container, Kicker, Mark } from '@/components/primitives';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Link } from 'react-router-dom';

const CHAPTERS = [
  {
    id: 'cap-i',
    num: 'I',
    label: 'Il problema',
    title: 'Il software ha smesso di andare in cantiere.',
    paragraphs: [
      "Negli ultimi vent'anni il mondo del lavoro è stato riscritto dal software. Le banche hanno avuto Stripe, i magazzini hanno avuto Shopify, le sale riunioni hanno avuto Notion. L'edilizia — il 13% del PIL europeo, dodici milioni di persone, gli edifici dentro cui tutti gli altri lavorano — ha avuto WhatsApp.",
      'Non è una battuta. È letteralmente la verità operativa. Il capocantiere parla nel gruppo. Il geometra fa screenshot del gruppo. La direzione tecnica chiede al geometra un Excel. L\'Excel viene fotografato e rispedito nel gruppo. A fine settimana qualcuno deve provare a ricostruire cosa è successo.',
      'Questo non è un problema di alfabetizzazione digitale. È un problema di rispetto. Nessuno ha mai costruito strumenti pensati davvero per chi tiene in mano il martello pneumatico. E quando provi a forzare un foreman dentro Jira, il foreman vince — perché ha cose più importanti da fare.',
    ],
    pull: 'Il foreman vince sempre. È così che funziona.',
  },
  {
    id: 'cap-ii',
    num: 'II',
    label: 'Le persone',
    title: 'Per chi tiene in piedi il Paese.',
    paragraphs: [
      "Pensiamo a chi sta dentro un cantiere alle sei e quaranta di un martedì di marzo. Il capocantiere che ha quarantatré anni, due figli, una schiena che ricorda gli ultimi venti inverni. Il geometra che è entrato in azienda nel 2011 con un sogno preciso — fare il direttore lavori — e oggi passa metà delle sere a digitare i rapportini che non ha avuto tempo di chiudere in giornata.",
      'Il direttore tecnico che la mattina apre quattordici tab di Excel, una per cantiere, e ogni numero che vede è già vecchio di due settimane. Il fondatore, che a sessantun anni ha costruito un\'impresa da quarantasette milioni di fatturato e ancora oggi firma personalmente i SAL perché non si fida dei sistemi che ha provato.',
      'BOSS è scritto per loro. Non per gli analisti, non per i consulenti di processo, non per i venture capitalist. Per le persone che si sporcano gli scarponi.',
    ],
    pull: "BOSS è scritto per chi si sporca gli scarponi.",
  },
  {
    id: 'cap-iii',
    num: 'III',
    label: 'Il salto',
    title: 'Una macchina che capisce il cantiere.',
    paragraphs: [
      "Per anni 'AI nell'edilizia' ha voluto dire previsione di ritardi su Gantt che nessuno aggiornava. Oggi vuol dire una cosa diversa. Vuol dire che un capocantiere può parlare al telefono come parlerebbe a un collega, in italiano stretto, e ottenere indietro un giornale di cantiere formattato, controllato, riconciliato.",
      "Vuol dire che il computista non passa più sei ore a Pasqua a riconciliare un SAL — il SAL è già pronto, già confrontato con il preventivo, già esportato in formato STR. Vuol dire che la compliance non è più una sorpresa che esplode il giorno dell'ispezione, ma uno stato continuo, sempre verde, sempre pronto.",
      "Quello che è cambiato non è il problema. È la tecnologia che possiamo applicare al problema. E noi crediamo che applicarla qui — al cantiere, al fornitore, al SAL, alla sicurezza — sia la cosa più utile che si possa fare con questa decade di AI.",
    ],
    pull: "Quello che è cambiato non è il problema. È la tecnologia.",
  },
  {
    id: 'cap-iv',
    num: 'IV',
    label: 'La regola',
    title: 'L\'edilizia non va reinventata. Va rispettata.',
    paragraphs: [
      "Questa è la riga che ci siamo scritti sul muro dell'ufficio. È la regola che governa ogni decisione di prodotto che prendiamo.",
      "Significa: il vocabolario è quello del cantiere. Diciamo giornale, non 'daily report'. Diciamo SAL, non 'progress claim'. Diciamo capocantiere, non 'site manager'. Significa: la app di campo non chiede mai più di tre tocchi per inviare. Significa: non chiediamo a nessuno di imparare un workflow nuovo — siamo noi che dobbiamo capire il loro.",
      "Significa anche che diciamo di no a tante cose. Niente gamification. Niente badge. Niente notifiche push alle nove di sera. Niente video onboarding di trentadue minuti. Niente teatro. Il rispetto si dimostra togliendo, non aggiungendo.",
    ],
    pull: 'Il rispetto si dimostra togliendo, non aggiungendo.',
  },
  {
    id: 'cap-v',
    num: 'V',
    label: 'Il futuro',
    title: 'Cosa stiamo costruendo davvero.',
    paragraphs: [
      "BOSS oggi è una piattaforma che chiude un giornale di cantiere in meno di un minuto. Bello, utile, già al lavoro nel pilota che abbiamo aperto con Almacis, a Pescara.",
      "Ma quello che stiamo costruendo — il vero progetto — è più grande. È il sistema operativo dell'impresa edile europea. Il livello sotto cui non si scende. Il posto da cui escono i giornali, i SAL, le fatture, i bandi, le offerte ai clienti, le revisioni delle squadre, gli inviti alle ispezioni, le firme dei subappaltatori, i fascicoli tecnici per la consegna.",
      "Uno strato unico. Un vocabolario unico. Una memoria unica per ogni mattone posato in un cantiere europeo.",
    ],
    pull: 'Una memoria unica per ogni mattone posato.',
  },
  {
    id: 'cap-vi',
    num: 'VI',
    label: 'Le persone che cerchiamo',
    title: 'Il Sud ha le eccellenze. Le vogliamo qui, non altrove.',
    paragraphs: [
      "Ogni anno le persone più in gamba del Sud fanno la valigia. Milano, Londra, Berlino — ovunque tranne restare. Non è colpa loro: nessuno ha mai costruito qui qualcosa di abbastanza grande, abbastanza serio, abbastanza internazionale da valerne la pena.",
      "Noi vogliamo essere quella cosa. Un progetto vero, internazionale, competitivo — figo — con sede al Sud e non a Milano. Non per bandiera, non per marketing. Perché crediamo che le persone giuste, messe nel posto giusto, da qui possano costruire qualcosa di enorme.",
      "Non cerchiamo curricula pieni di master né righe di esperienza scritte bene. Cerchiamo tre cose sole, e sono le uniche che chiediamo: una voglia matta di spaccare, voglia di apprendere, curiosità e passione vere per quello che si fa. Il resto — competenze, mestiere, strumenti — lo costruiamo insieme.",
    ],
    pull: 'Non cerchiamo curricula. Cerchiamo gente con voglia matta di spaccare.',
  },
];

export default function ManifestoPage() {
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 60, damping: 18, mass: 1 });

  const [activeId, setActiveId] = useState<string>(CHAPTERS[0].id);

  useEffect(() => {
    const els = CHAPTERS.map((c) => document.getElementById(c.id)).filter(
      (el): el is HTMLElement => Boolean(el),
    );
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <article className="relative">
      {/* Reading-progress bar */}
      <motion.div
        aria-hidden
        style={{ scaleX: progress }}
        className="fixed left-0 right-0 top-16 z-40 h-px origin-left bg-primary/60"
      />

      {/* Cover */}
      <section className="relative isolate overflow-hidden pt-36 pb-24 md:pt-44 md:pb-32">
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(50% 60% at 50% 0%, color-mix(in oklch, var(--primary) 18%, transparent), transparent 70%)',
          }}
        />
        <div
          aria-hidden
          className="blueprint-grid absolute inset-0 -z-10 opacity-[0.05]"
          style={{ maskImage: 'radial-gradient(60% 50% at 50% 0%, black, transparent 75%)' }}
        />

        <Container className="relative">
          <Kicker label="Manifesto · 0.1" index="00" />
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="boss-display mt-8 max-w-[18ch] text-balance text-[60px] leading-[0.96] md:text-[128px]"
          >
            Vogliamo essere <br /> il sistema operativo <br />
            di <Mark>chi costruisce</Mark>.
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.9 }}
            className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-12 md:max-w-3xl"
          >
            <MetaCell label="Autori" value="Il team fondatore di BOSS" />
            <MetaCell label="Luogo" value="Abruzzo" />
            <MetaCell label="Data" value="25 maggio 2026" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.9 }}
            className="mt-16 max-w-2xl text-pretty text-[18px] leading-[1.65] text-fg-2"
          >
            Questo documento spiega perché esistiamo. Lo scriviamo all'inizio perché vogliamo poterci tornare,
            tra cinque anni, e capire se siamo rimasti fedeli a noi stessi. È pensato per i nostri clienti,
            per chi vorrebbe entrare nel team, e per chi vorrà sfidarci nei prossimi anni.
          </motion.p>
        </Container>
      </section>

      <Container>
        <Separator className="opacity-60" />
      </Container>

      {/* Body — sticky TOC + chapters */}
      <section className="relative pt-20 pb-28 md:pb-36">
        <Container>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[220px_1fr] lg:gap-20">
            <aside className="hidden lg:block">
              <div className="sticky top-28">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  Capitoli
                </div>
                <nav className="mt-5 flex flex-col gap-3">
                  {CHAPTERS.map((c) => {
                    const active = c.id === activeId;
                    return (
                      <a
                        key={c.id}
                        href={`#${c.id}`}
                        className="group flex items-start gap-3 text-[13px] leading-tight transition-colors"
                      >
                        <span
                          className={`mt-0.5 inline-block w-6 font-mono text-[10px] ${
                            active ? 'text-primary' : 'text-fg-disabled'
                          }`}
                        >
                          {c.num}
                        </span>
                        <span
                          className={`transition-colors ${
                            active ? 'text-foreground' : 'text-fg-2 group-hover:text-foreground'
                          }`}
                        >
                          {c.label}
                        </span>
                      </a>
                    );
                  })}
                </nav>
              </div>
            </aside>

            <div className="space-y-32 md:space-y-40">
              {CHAPTERS.map((c) => (
                <Chapter key={c.id} chapter={c} />
              ))}

              {/* Closing */}
              <ClosingSignature />
            </div>
          </div>
        </Container>
      </section>

      {/* Footer CTA */}
      <section className="relative border-t border-border py-28 md:py-36">
        <Container>
          <div className="grid grid-cols-1 items-end gap-10 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <Kicker index="·" label="E adesso" />
              <h2 className="boss-display mt-5 text-balance text-[44px] md:text-[80px]">
                Se ti riconosci in queste righe, <br />
                <Mark>parliamone.</Mark>
              </h2>
            </div>
            <div className="flex flex-col gap-4 md:items-end">
              <Button asChild size="xl" variant="default">
                <a href="mailto:hello@bossai.build">Scrivici una mail →</a>
              </Button>
              <Button asChild size="xl" variant="outline">
                <Link to="/prodotto">Vedi cosa fa BOSS →</Link>
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </article>
  );
}

function MetaCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1.5 text-[15px] text-foreground">{value}</div>
    </div>
  );
}

function Chapter({ chapter }: { chapter: (typeof CHAPTERS)[number] }) {
  return (
    <motion.section
      id={chapter.id}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-2xl scroll-mt-28"
    >
      <div className="flex items-baseline gap-4 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        <span className="text-primary">§ {chapter.num}</span>
        <span className="h-px flex-1 bg-border" />
        <span>{chapter.label}</span>
      </div>
      <h2 className="boss-h1 mt-7 text-balance text-[40px] leading-[1.05] md:text-[60px]">
        {chapter.title}
      </h2>

      <div className="mt-10 space-y-6">
        {chapter.paragraphs.map((p, i) => (
          <p
            key={i}
            className="text-pretty text-[17px] leading-[1.7] text-fg-2"
            style={{ fontFamily: 'var(--font-sans)' }}
          >
            {p}
          </p>
        ))}
      </div>

      <PullQuote>{chapter.pull}</PullQuote>
    </motion.section>
  );
}

function PullQuote({ children }: { children: React.ReactNode }) {
  return (
    <motion.blockquote
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="relative mt-14 border-l-2 border-primary pl-6 text-balance text-[26px] leading-[1.25] tracking-tight text-foreground md:text-[34px]"
    >
      {children}
    </motion.blockquote>
  );
}

function ClosingSignature() {
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.9 }}
      className="max-w-2xl scroll-mt-28"
    >
      <div className="flex items-baseline gap-4 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        <span className="text-primary">§ Fine</span>
        <span className="h-px flex-1 bg-border" />
        <span>Firma</span>
      </div>
      <p className="mt-10 text-balance text-[24px] leading-[1.4] text-foreground md:text-[32px]">
        Costruiamo per chi costruisce. <br />
        Costruiamo lentamente. <br />
        Costruiamo per restare.
      </p>
      <div className="mt-12 grid grid-cols-2 gap-6 border-t border-border pt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground md:grid-cols-4">
        <SigField label="Firmato" value="Il team BOSS" />
        <SigField label="Luogo" value="Abruzzo" />
        <SigField label="Data" value="25 / 05 / 2026" />
        <SigField label="Revisione" value="0.1 · Maggio 2026" />
      </div>
    </motion.section>
  );
}

function SigField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-fg-disabled">{label}</div>
      <div className="mt-1 text-foreground">{value}</div>
    </div>
  );
}
